import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  UserCog, ArrowRight, ShoppingCart, Trash2, Plus, Undo2, Redo2, LogOut,
  Package, Images, History, Search, CheckCircle2, X, Home, ZoomIn, PhoneCall, Truck, Lock,
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { slug, unitOf, setFabricUnits, formatDateTime, SWATCHES, deriveFabricCodes, itemMatchesQuery } from "@/lib/fabricData";
import {
  getStock, getFabrics, getColors, fileUrl, formatApiError,
  coadminMe, coadminLogin, coadminSearchCustomers,
  coadminPlaceOrder, coadminGetOrders, coadminGetCatalog,
  coadminGetAllOrders, coadminAmendDelivered,
  coadminAlterItem, coadminAlterCustomer, coadminDeleteItem, coadminRestoreItem,
} from "@/lib/api";
import { ImageZoom } from "@/components/ImageZoom";
import { DeliveredCell } from "@/components/DeliveredCell";
import { AlteredLabel, AlteredOriginalLine, AlteredCustomerName, DeletedLabel, EditPencil, AlterItemDialog, CustomerPickerDialog } from "@/components/orderAlter";

const TOKEN_KEY = "cincstock_coadmin_token";
const fmt = (n) => Number(n).toLocaleString(undefined, { maximumFractionDigits: 2 });

function LoginScreen({ onLogin }) {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    if (!username.trim() || !password.trim()) return toast.error("Enter username and password");
    setBusy(true);
    try {
      const d = await coadminLogin(username.trim(), password.trim());
      localStorage.setItem(TOKEN_KEY, d.token);
      onLogin(d.token);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Login failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-[#0A0A0A]">
      <form onSubmit={submit} className="w-full max-w-sm" data-testid="coadmin-login-form">
        <button
          type="button"
          onClick={() => navigate("/")}
          data-testid="coadmin-login-home"
          className="mb-6 inline-flex items-center gap-2 text-xs uppercase tracking-wider text-white/40 hover:text-white transition-colors"
        >
          <Home className="h-3.5 w-3.5" /> Back to portal
        </button>
        <div className="h-10 w-10 border border-white/20 flex items-center justify-center mb-6">
          <UserCog className="h-5 w-5 text-white" />
        </div>
        <h1 className="font-display font-black text-4xl tracking-tighter text-white">Co-Admin</h1>
        <p className="text-white/40 text-sm mt-2 mb-8">Sign in to place orders on behalf of customers.</p>
        <div className="space-y-5">
          <div className="space-y-2">
            <Label className="text-xs uppercase tracking-wider text-white/50">Username</Label>
            <Input
              data-testid="coadmin-login-username"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              placeholder="Username"
              className="rounded-none bg-[#141414] border-white/10 text-white placeholder:text-white/30"
            />
          </div>
          <div className="space-y-2">
            <Label className="text-xs uppercase tracking-wider text-white/50">Password</Label>
            <Input
              type="password"
              data-testid="coadmin-login-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Password"
              className="rounded-none bg-[#141414] border-white/10 text-white placeholder:text-white/30"
            />
          </div>
        </div>
        <Button
          type="submit"
          disabled={busy}
          data-testid="coadmin-login-submit"
          className="mt-8 w-full rounded-none bg-white text-black hover:bg-white/90 text-sm uppercase tracking-wider font-semibold"
        >
          {busy ? "Signing in…" : <>Sign In <ArrowRight className="h-4 w-4 ml-2" /></>}
        </Button>
      </form>
    </div>
  );
}

function CustomerPicker({ token, onPick }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const timer = useRef(null);

  useEffect(() => {
    if (timer.current) clearTimeout(timer.current);
    if (q.trim().length < 3) { setResults([]); return; }
    setLoading(true);
    timer.current = setTimeout(async () => {
      try {
        const d = await coadminSearchCustomers(token, q.trim());
        setResults(d);
      } catch { setResults([]); }
      finally { setLoading(false); }
    }, 300);
    return () => timer.current && clearTimeout(timer.current);
  }, [q, token]);

  return (
    <div className="max-w-md" data-testid="coadmin-customer-picker">
      <Label className="text-xs uppercase tracking-wider text-white/50 block mb-2">Search customer (type at least 3 letters)</Label>
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-white/30" />
        <Input
          data-testid="coadmin-customer-search"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Customer name"
          className="rounded-none pl-9 bg-[#141414] border-white/10 text-white placeholder:text-white/30"
        />
      </div>
      {q.trim().length >= 3 && (
        <div className="mt-2 border border-white/10 bg-[#0A0A0A] divide-y divide-white/5 max-h-64 overflow-auto" data-testid="coadmin-customer-results">
          {loading && <div className="px-4 py-3 text-sm text-white/40">Searching…</div>}
          {!loading && results.length === 0 && <div className="px-4 py-3 text-sm text-white/40">No matching customers</div>}
          {!loading && results.map((c) => (
            <button
              key={c.id}
              onClick={() => onPick(c)}
              data-testid={`coadmin-customer-option-${c.id}`}
              className="w-full text-left px-4 py-3 text-sm text-white hover:bg-white hover:text-black transition-colors"
            >
              {c.name}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function StockBrowser({ stock, fabrics, onAdd }) {
  const fabricNames = useMemo(() => {
    const present = new Set(stock.map((s) => s.fabric));
    const ordered = fabrics.map((f) => f.name).filter((n) => present.has(n));
    stock.forEach((s) => { if (!ordered.includes(s.fabric)) ordered.push(s.fabric); });
    return ordered;
  }, [stock, fabrics]);

  const [fabric, setFabric] = useState("");
  const [query, setQuery] = useState("");
  useEffect(() => { if (!fabric && fabricNames.length) setFabric(fabricNames[0]); }, [fabricNames, fabric]);

  const codeMap = useMemo(() => deriveFabricCodes(stock, fabrics), [stock, fabrics]);
  const searching = query.trim().length > 0;
  const rows = useMemo(() => {
    if (searching) {
      return stock.filter((s) => itemMatchesQuery(s, query, codeMap));
    }
    return stock.filter((s) => s.fabric === fabric);
  }, [stock, fabric, query, searching, codeMap]);

  if (!fabricNames.length) return <p className="text-sm text-white/40">No stock available yet.</p>;

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1 min-w-0">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-white/30" />
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            data-testid="coadmin-stock-search"
            placeholder="Search any fabric or colour…"
            className="rounded-none pl-9 bg-[#141414] border-white/10 text-white placeholder:text-white/30"
          />
          {searching && (
            <button onClick={() => setQuery("")} data-testid="coadmin-stock-search-clear" className="absolute right-2 top-1/2 -translate-y-1/2 h-6 w-6 flex items-center justify-center text-white/40 hover:text-white">
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
        {!searching && (
          <div className="sm:w-56 shrink-0">
            <Select value={fabric} onValueChange={setFabric}>
              <SelectTrigger data-testid="coadmin-fabric-select" className="rounded-none bg-[#141414] border-white/10 text-white">
                <SelectValue placeholder="Select fabric" />
              </SelectTrigger>
              <SelectContent>
                {fabricNames.map((n) => <SelectItem key={n} value={n}>{n}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        )}
      </div>

      {rows.length === 0 ? (
        <p className="text-sm text-white/40 border border-white/10 px-4 py-8 text-center" data-testid="coadmin-stock-no-results">No matching fabrics or colours.</p>
      ) : (
        <div className="border border-white/10 divide-y divide-white/5">
          {rows.map((it) => {
            const availKg = it.available_kg || 0;
            const inStock = availKg > 0;
            const hex = it.hex || SWATCHES[it.color] || "#666";
            return (
              <div key={it.id || `${it.fabric}-${it.color}`} className="flex items-center gap-3 px-4 py-3" data-testid={`coadmin-stock-row-${slug(it.fabric)}-${slug(it.color)}`}>
                <span className="h-6 w-6 border border-white/20 shrink-0" style={{ backgroundColor: hex }} />
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-white truncate">{it.color}</p>
                  {searching && <p className="text-[11px] text-white/40 truncate">{it.fabric}</p>}
                </div>
                <span className={`text-sm tabular text-right shrink-0 ${inStock ? "text-emerald-400" : "text-red-400"}`} data-testid={`coadmin-stock-qty-${slug(it.fabric)}-${slug(it.color)}`}>
                  {inStock ? `${fmt(availKg)} ${unitOf(it.fabric)}` : "Out of stock"}
                </span>
                {onAdd && (
                  <Button
                    size="sm"
                    onClick={() => onAdd(it)}
                    data-testid={`coadmin-add-${slug(it.fabric)}-${slug(it.color)}`}
                    className="rounded-none h-8 bg-white text-black hover:bg-white/90 text-xs uppercase tracking-wider shrink-0"
                  >
                    <Plus className="h-3.5 w-3.5 mr-1" /> Add
                  </Button>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default function CoAdminPortal({ injectedToken, onExitEmbed }) {
  const navigate = useNavigate();
  const embedded = !!injectedToken;
  const [token, setToken] = useState(() => injectedToken || localStorage.getItem(TOKEN_KEY) || "");
  const [ready, setReady] = useState(false);
  const [showStock, setShowStock] = useState(false);
  const [view, setView] = useState("build");

  const [stock, setStock] = useState([]);
  const [fabrics, setFabrics] = useState([]);
  const [colors, setColors] = useState([]);
  const [alterItemCtx, setAlterItemCtx] = useState(null);
  const [alterCustomerCtx, setAlterCustomerCtx] = useState(null);
  const [catalog, setCatalog] = useState([]);
  const [orders, setOrders] = useState([]);
  const [historyTotal, setHistoryTotal] = useState(0);
  const [historyLimit, setHistoryLimit] = useState(20);
  const [allOrders, setAllOrders] = useState([]);
  const [allTotal, setAllTotal] = useState(0);
  const [allLimit, setAllLimit] = useState(20);
  const [delivQuery, setDelivQuery] = useState("");
  const [zoomImg, setZoomImg] = useState(null);

  // Build order state
  const [customer, setCustomer] = useState(null);
  const [cart, setCart] = useState([]);
  const [undoStack, setUndoStack] = useState([]);
  const [redoStack, setRedoStack] = useState([]);
  const [addItem, setAddItem] = useState(null);
  const [cartOpen, setCartOpen] = useState(false);
  const [qty, setQty] = useState("");
  const [message, setMessage] = useState("");
  const [urgent, setUrgent] = useState(false);
  const [requiredBy, setRequiredBy] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(null);

  const logout = () => {
    if (embedded) { onExitEmbed && onExitEmbed(); return; }
    localStorage.removeItem(TOKEN_KEY);
    setToken("");
    setReady(false);
    navigate("/");
  };

  // Keep the injected (admin-delegated) token in sync if it changes.
  useEffect(() => { if (injectedToken) setToken(injectedToken); }, [injectedToken]);

  // Validate token + fetch capabilities
  useEffect(() => {
    if (!token) { setReady(true); return; }
    (async () => {
      try {
        const me = await coadminMe(token);
        setShowStock(!!me.show_stock);
        setReady(true);
      } catch {
        if (!embedded) { localStorage.removeItem(TOKEN_KEY); setToken(""); }
        setReady(true);
      }
    })();
  }, [token]); // eslint-disable-line

  const loadStock = async () => {
    try {
      const [s, f, cols] = await Promise.all([getStock(), getFabrics(), getColors()]);
      const hexByName = {};
      (cols || []).forEach((c) => { if (c && c.name) hexByName[c.name] = c.hex; });
      const merged = (s || []).map((it) => ({ ...it, hex: it.hex || hexByName[it.color] }));
      setFabrics(f); setFabricUnits(f); setStock(merged); setColors(cols || []);
    } catch { /* ignore */ }
  };
  const loadCatalog = async () => { try { setCatalog(await coadminGetCatalog(token)); } catch {} };
  const loadOrders = async () => { try { const r = await coadminGetOrders(token, { skip: 0, limit: historyLimit }); setOrders(r.items); setHistoryTotal(r.total); } catch {} };
  const loadAllOrders = async () => { try { const r = await coadminGetAllOrders(token, { q: delivQuery, skip: 0, limit: allLimit }); setAllOrders(r.items); setAllTotal(r.total); } catch {} };

  useEffect(() => {
    if (!token || !ready) return;
    loadStock();
    // eslint-disable-next-line
  }, [token, ready]);

  useEffect(() => {
    if (!token) return;
    if (view === "catalog") loadCatalog();
    if (view === "history") loadOrders();
    if (view === "deliveries") loadAllOrders();
    /* eslint-disable-next-line */
  }, [view]);

  // Deliveries: refetch (backend search over full DB) on query / batch change.
  useEffect(() => {
    if (!token || view !== "deliveries") return;
    const t = setTimeout(() => loadAllOrders(), 250);
    return () => clearTimeout(t);
    /* eslint-disable-next-line */
  }, [delivQuery, allLimit]);
  useEffect(() => { setAllLimit(20); }, [delivQuery]);
  // History: refetch when the loaded batch grows.
  useEffect(() => {
    if (!token || view !== "history") return;
    loadOrders();
    /* eslint-disable-next-line */
  }, [historyLimit]);

  // If admin disabled View Stock while we're on it, bounce back
  useEffect(() => { if (view === "stock" && !showStock) setView("build"); }, [view, showStock]);

  // ----- Undo / Redo (up to 10 steps) -----
  const commit = (next) => {
    setUndoStack((s) => [...s, cart].slice(-10));
    setRedoStack([]);
    setCart(next);
  };
  const undo = () => {
    if (!undoStack.length) return;
    const prev = undoStack[undoStack.length - 1];
    setUndoStack((s) => s.slice(0, -1));
    setRedoStack((r) => [...r, cart].slice(-10));
    setCart(prev);
  };
  const redo = () => {
    if (!redoStack.length) return;
    const nxt = redoStack[redoStack.length - 1];
    setRedoStack((r) => r.slice(0, -1));
    setUndoStack((s) => [...s, cart].slice(-10));
    setCart(nxt);
  };

  const handleCoadminDelivered = async (order, index, delivered_kg) => {
    await coadminAmendDelivered(token, order.id, index, delivered_kg);
    loadAllOrders();
  };

  const handleCoadminAlterItem = async (fabric, color) => {
    if (!alterItemCtx) return;
    await coadminAlterItem(token, alterItemCtx.order.id, alterItemCtx.index, fabric, color);
    toast.success("Item altered");
    loadAllOrders();
  };
  const handleCoadminAlterCustomer = async (cust) => {
    if (!alterCustomerCtx) return;
    try {
      await coadminAlterCustomer(token, alterCustomerCtx.order.id, cust.id);
      toast.success("Customer changed on this order");
      loadAllOrders();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const handleCoadminDeleteItem = async (order, index) => {
    if (!window.confirm("Delete this item? Its quantity is returned to stock; the row stays visible struck-through as 'co-ad del'.")) return;
    try {
      await coadminDeleteItem(token, order.id, index);
      toast.success("Item deleted — qty returned to stock");
      loadAllOrders();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const handleCoadminRestoreItem = async (order, index) => {
    try {
      await coadminRestoreItem(token, order.id, index);
      toast.success("Item restored — qty re-deducted from stock");
      loadAllOrders();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const qtyInCart = (fabric, color) => {
    const it = cart.find((i) => i.fabric === fabric && i.color === color);
    return it ? it.quantity_kg : 0;
  };

  const confirmAdd = () => {
    const n = parseFloat(qty);
    if (!n || n <= 0) return toast.error("Enter a quantity");
    const already = qtyInCart(addItem.fabric, addItem.color);
    if (n + already > (addItem.available_kg ?? 0))
      return toast.error("Please call 8010307363 / 7020006164 for the availability of the fabric.");
    const existing = cart.find((i) => i.fabric === addItem.fabric && i.color === addItem.color);
    const next = existing
      ? cart.map((i) => (i.fabric === addItem.fabric && i.color === addItem.color ? { ...i, quantity_kg: i.quantity_kg + n } : i))
      : [...cart, { fabric: addItem.fabric, color: addItem.color, hex: addItem.hex, quantity_kg: n }];
    commit(next);
    setAddItem(null); setQty("");
    toast.success("Added to order");
  };

  const removeItem = (fabric, color) => commit(cart.filter((i) => !(i.fabric === fabric && i.color === color)));

  const updateCartQty = (fabric, color, value) => {
    const q = parseFloat(value);
    commit(cart.map((i) => (i.fabric === fabric && i.color === color ? { ...i, quantity_kg: isNaN(q) ? 0 : q } : i)));
  };

  const resetBuild = () => {
    setCart([]); setUndoStack([]); setRedoStack([]); setMessage(""); setUrgent(false); setRequiredBy("");
  };

  const submitOrder = async () => {
    if (!customer) return toast.error("Select a customer first");
    if (!cart.length) return toast.error("Your order is empty");
    if (cart.some((i) => i.quantity_kg <= 0)) return toast.error("Every item needs a quantity greater than 0");
    setSubmitting(true);
    try {
      const res = await coadminPlaceOrder(token, {
        customer_id: customer.id,
        items: cart.map((i) => ({ fabric: i.fabric, color: i.color, quantity_kg: i.quantity_kg })),
        message: message.trim() || null,
        urgent,
        required_by: requiredBy || null,
      });
      setSubmitted(res.order);
      setCartOpen(false);
      resetBuild();
      setCustomer(null);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not place order");
    } finally {
      setSubmitting(false);
    }
  };

  const cartUnit = useMemo(() => {
    const units = new Set(cart.map((i) => unitOf(i.fabric)));
    return units.size === 1 ? [...units][0] : "";
  }, [cart]);
  const cartTotal = useMemo(() => cart.reduce((s, i) => s + i.quantity_kg, 0), [cart]);

  if (!ready) return <div className="min-h-screen bg-[#0A0A0A]" />;
  if (!token) return <LoginScreen onLogin={setToken} />;

  const NavBtn = ({ id, icon: Icon, label, tid }) => (
    <button
      onClick={() => setView(id)}
      data-testid={tid}
      className={`inline-flex items-center gap-2 px-3 py-2 text-xs uppercase tracking-wider border transition-colors ${view === id ? "bg-white text-black border-white" : "border-white/15 text-white/70 hover:text-white"}`}
    >
      <Icon className="h-3.5 w-3.5" /> {label}
    </button>
  );

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white">
      {/* Header */}
      <div className="border-b border-white/10 bg-[#141414]">
        <div className="max-w-6xl mx-auto px-4 py-3 flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <div className="h-8 w-8 border border-white/20 flex items-center justify-center"><UserCog className="h-4 w-4" /></div>
            <span className="font-display font-black text-lg tracking-tighter">CiNCStock <span className="text-white/40 text-sm">· Co-Admin</span></span>
          </div>
          <Button onClick={logout} data-testid="coadmin-logout" variant="outline" className="rounded-none h-9 border-white/20 bg-transparent text-white hover:bg-white hover:text-black text-xs uppercase tracking-wider">
            <LogOut className="h-3.5 w-3.5 mr-1" /> Log Out
          </Button>
        </div>
        <div className="max-w-6xl mx-auto px-4 pb-3 flex flex-wrap gap-2">
          <NavBtn id="build" icon={ShoppingCart} label="Build Order" tid="coadmin-nav-build" />
          {showStock && <NavBtn id="stock" icon={Package} label="View Stock" tid="coadmin-nav-stock" />}
          <NavBtn id="catalog" icon={Images} label="Catalogue" tid="coadmin-nav-catalog" />
          <NavBtn id="deliveries" icon={Truck} label="Deliveries" tid="coadmin-nav-deliveries" />
          <NavBtn id="history" icon={History} label="Order History" tid="coadmin-nav-history" />
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-6">
        {/* BUILD ORDER */}
        {view === "build" && (
          <div className="space-y-6 pb-28" data-testid="coadmin-build-view">
            <h1 className="font-display font-black text-3xl tracking-tight">Build Order</h1>
            {!customer ? (
              <CustomerPicker token={token} onPick={(c) => { setCustomer(c); resetBuild(); }} />
            ) : (
              <>
                <div className="flex flex-wrap items-center justify-between gap-3 border border-white/15 bg-[#141414] px-4 py-3">
                  <div>
                    <p className="text-[10px] uppercase tracking-[0.2em] text-white/40">Ordering for</p>
                    <p className="text-lg font-display font-black" data-testid="coadmin-selected-customer">CO-AD — {customer.name}</p>
                  </div>
                  <Button onClick={() => { setCustomer(null); resetBuild(); }} data-testid="coadmin-change-customer" variant="outline" className="rounded-none h-8 border-white/20 bg-transparent text-white hover:bg-white hover:text-black text-xs uppercase tracking-wider">Change</Button>
                </div>

                <div>
                  <h2 className="text-sm uppercase tracking-wider text-white/60 mb-3">Add items</h2>
                  <StockBrowser stock={stock} fabrics={fabrics} onAdd={(it) => { setAddItem(it); setQty(""); }} />
                </div>
              </>
            )}
          </div>
        )}

        {/* Sticky order bar — always visible, no scrolling needed */}
        {view === "build" && customer && (
          <div className="fixed bottom-0 left-0 right-0 z-30 border-t border-white/10 bg-[#141414]/95 backdrop-blur px-4 py-3" style={{ paddingBottom: "max(0.75rem, env(safe-area-inset-bottom))" }} data-testid="coadmin-order-bar">
            <div className="max-w-6xl mx-auto flex items-center gap-3">
              <div className="flex-1 min-w-0">
                <p className="text-[10px] uppercase tracking-[0.2em] text-white/40">Your order</p>
                <p className="text-sm text-white truncate" data-testid="coadmin-order-bar-summary">{cart.length} item{cart.length === 1 ? "" : "s"} · {fmt(cartTotal)} {cartUnit}</p>
              </div>
              <Button onClick={() => setCartOpen(true)} disabled={!cart.length} data-testid="coadmin-open-cart" className="rounded-none h-11 bg-white text-black hover:bg-white/90 text-sm uppercase tracking-wider font-semibold disabled:opacity-40">
                <ShoppingCart className="h-4 w-4 mr-2" /> Review &amp; Submit
              </Button>
            </div>
          </div>
        )}

        {/* VIEW STOCK (read-only) */}
        {view === "stock" && showStock && (
          <div className="space-y-6" data-testid="coadmin-stock-view">
            <h1 className="font-display font-black text-3xl tracking-tight">View Stock</h1>
            <p className="text-sm text-white/40">Read-only — actual available quantities. Search any fabric or colour below.</p>
            <StockBrowser stock={stock} fabrics={fabrics} onAdd={null} />
          </div>
        )}

        {/* CATALOGUE */}
        {view === "catalog" && (
          <div className="space-y-6" data-testid="coadmin-catalog-view">
            <h1 className="font-display font-black text-3xl tracking-tight">Catalogue</h1>
            {catalog.length === 0 ? (
              <p className="text-sm text-white/40">No catalogue images yet.</p>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                {catalog.map((c) => (
                  <button key={c.id} onClick={() => setZoomImg(fileUrl(c.storage_path))} data-testid={`coadmin-catalog-img-${c.id}`} className="group relative border border-white/10 overflow-hidden aspect-square bg-[#141414]">
                    <img src={fileUrl(c.storage_path)} alt={c.title || "catalog"} className="w-full h-full object-cover transition-transform group-hover:scale-105" />
                    <span className="absolute bottom-1 right-1 h-7 w-7 flex items-center justify-center bg-black/60 text-white opacity-0 group-hover:opacity-100 transition-opacity"><ZoomIn className="h-4 w-4" /></span>
                    {c.title && <span className="absolute bottom-0 left-0 right-8 bg-black/70 text-[10px] text-white px-2 py-1 truncate">{c.title}</span>}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {/* DELIVERIES — amend actual delivered quantities on any order */}
        {view === "deliveries" && (
          <div className="space-y-5" data-testid="coadmin-deliveries-view">
            <div>
              <h1 className="font-display font-black text-3xl tracking-tight">Deliveries</h1>
              <p className="text-sm text-white/40">Enter the actual delivered quantity for each item. Stock updates automatically.</p>
            </div>
            <div className="relative max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-white/30" />
              <Input
                value={delivQuery}
                onChange={(e) => setDelivQuery(e.target.value)}
                data-testid="coadmin-deliveries-search"
                placeholder="Search by customer name…"
                className="rounded-none pl-9 bg-[#141414] border-white/10 text-white placeholder:text-white/30"
              />
            </div>
            {allOrders.length === 0 ? (
              <p className="text-sm text-white/40 border border-white/10 px-4 py-8 text-center">No orders found.</p>
            ) : (
                <div className="space-y-3">
                  {allOrders.map((o) => {
                    const locked = (o.status || "Pending") !== "Pending";
                    const cancelled = o.status === "Cancelled";
                    return (
                      <div key={o.id} className="border border-white/10 bg-[#0A0A0A]" data-testid={`coadmin-delivery-card-${o.id}`}>
                        <div className="flex flex-wrap items-center justify-between gap-2 px-4 py-3 border-b border-white/10 bg-[#141414]">
                          <div className="min-w-0">
                            <p className="text-sm font-medium flex items-center flex-wrap gap-x-1">
                              {o.is_coadmin ? <span className="text-purple-300">CO-AD — </span> : null}
                              <span className="truncate" data-testid={`coadmin-client-name-${o.id}`}>{o.client_name}</span>
                              <AlteredCustomerName order={o} testidPrefix={`coadmin-client-altered-${o.id}`} />
                              {!locked && !cancelled && <EditPencil onClick={() => setAlterCustomerCtx({ order: o })} testid={`coadmin-customer-edit-${o.id}`} title="Change customer" />}
                              {o.company_name ? <span className="text-white/40"> · {o.company_name}</span> : null}
                            </p>
                            <p className="text-xs text-white/50">{o.created_at ? formatDateTime(o.created_at) : "—"}</p>
                          </div>
                          <div className="flex items-center gap-2">
                            {locked && (
                              <span data-testid={`coadmin-delivery-locked-${o.id}`} className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider px-2 py-1 border border-amber-500/40 bg-amber-500/10 text-amber-300">
                                <Lock className="h-3 w-3" /> Locked
                              </span>
                            )}
                            <span className="text-[10px] uppercase tracking-[0.2em] px-2 py-1 border border-white/20 text-white/70">{o.status || "Pending"}</span>
                          </div>
                        </div>
                        {locked && (
                          <p className="px-4 py-1.5 text-[11px] text-amber-300/70 border-b border-white/5 bg-amber-500/5">Actioned by admin ({o.status}) — deliveries can no longer be amended here.</p>
                        )}
                        <div className="divide-y divide-white/5">
                          <div className="flex items-center gap-3 px-4 py-1.5 text-[9px] uppercase tracking-[0.15em] text-white/30">
                            <span className="w-6 shrink-0 text-right">#</span>
                            <span className="flex-1 min-w-0">Item</span>
                            <span className="tabular text-right">Ordered</span>
                            <span className="w-[104px] text-right">Delivered</span>
                          </div>
                          {(o.items || []).map((it, i) => {
                            const rejected = it.status === "Rejected";
                            const deleted = !!it.deleted_by;
                            const struck = rejected || deleted;
                            return (
                              <div key={i} className="flex items-center gap-3 px-4 py-2 text-sm" data-testid={`coadmin-delivery-item-${o.id}-${i}`}>
                                <span className="w-6 shrink-0 text-right tabular text-xs text-white/40" data-testid={`coadmin-item-serial-${o.id}-${i}`}>{i + 1}.</span>
                                <span className="flex-1 min-w-0 flex flex-col">
                                  <AlteredOriginalLine item={it} testid={`coadmin-item-original-${o.id}-${i}`} />
                                  <span className={`flex items-center min-w-0 ${struck ? "text-white/40 line-through" : "text-white"}`}>
                                    <span className="truncate">{it.fabric} <span className="text-white/40">·</span> {it.color}</span>
                                    <AlteredLabel by={it.altered_by} testid={`coadmin-item-altered-label-${o.id}-${i}`} />
                                    <DeletedLabel by={it.deleted_by} testid={`coadmin-item-deleted-label-${o.id}-${i}`} />
                                  </span>
                                </span>
                                {!cancelled && !deleted && (
                                  <>
                                    {!locked && <EditPencil onClick={() => setAlterItemCtx({ order: o, index: i, item: it })} testid={`coadmin-item-edit-${o.id}-${i}`} title="Alter fabric / colour" />}
                                    <button onClick={() => handleCoadminDeleteItem(o, i)} data-testid={`coadmin-item-delete-${o.id}-${i}`} title="Delete item (returns qty to stock)" className="shrink-0 p-1 text-red-400/60 hover:text-red-400 transition-colors"><Trash2 className="h-3.5 w-3.5" /></button>
                                  </>
                                )}
                                {!cancelled && deleted && (
                                  <button onClick={() => handleCoadminRestoreItem(o, i)} data-testid={`coadmin-item-restore-${o.id}-${i}`} title="Undo delete — restore item & re-deduct stock" className="shrink-0 inline-flex items-center gap-1 text-[10px] uppercase tracking-wider text-amber-400/80 hover:text-amber-300 border border-amber-500/30 hover:border-amber-400 px-1.5 py-0.5 transition-colors"><Undo2 className="h-3 w-3" /> Undo</button>
                                )}
                                <span className="tabular text-white/80">{fmt(it.quantity_kg)} {unitOf(it.fabric)}</span>
                                <DeliveredCell
                                  item={it}
                                  unit={unitOf(it.fabric)}
                                  disabled={locked || cancelled || rejected || deleted}
                                  onSave={(v) => handleCoadminDelivered(o, i, v)}
                                  testid={`coadmin-delivery-delivered-${o.id}-${i}`}
                                />
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                </div>
            )}
            {allOrders.length < allTotal && (
              <div className="flex justify-center pt-2">
                <Button onClick={() => setAllLimit((l) => l + 20)} variant="outline" data-testid="coadmin-deliveries-load-more" className="rounded-none h-10 px-8 border-white/20 bg-transparent text-white hover:bg-white hover:text-black text-xs uppercase tracking-wider transition-colors">
                  Load More ({allTotal - allOrders.length} more)
                </Button>
              </div>
            )}
          </div>
        )}

        {/* ORDER HISTORY */}
        {view === "history" && (
          <div className="space-y-6" data-testid="coadmin-history-view">
            <h1 className="font-display font-black text-3xl tracking-tight">Order History</h1>
            {orders.length === 0 ? (
              <p className="text-sm text-white/40">You haven't placed any orders yet.</p>
            ) : (
              <div className="space-y-3">
                {orders.map((o) => (
                  <div key={o.id} className="border border-white/10 bg-[#0A0A0A]" data-testid={`coadmin-history-card-${o.id}`}>
                    <div className="flex flex-wrap items-center justify-between gap-2 px-4 py-3 border-b border-white/10 bg-[#141414]">
                      <div>
                        <p className="text-sm font-medium">CO-AD — {o.client_name}</p>
                        <p className="text-xs text-white/50">{o.created_at ? formatDateTime(o.created_at) : "—"}</p>
                      </div>
                      <div className="flex items-center gap-2">
                        {o.urgent && <span className="text-[10px] uppercase tracking-wider px-2 py-1 border border-red-500 bg-red-500/20 text-red-300">Urgent</span>}
                        <span className="text-[10px] uppercase tracking-[0.2em] px-2 py-1 border border-white/20 text-white/70">{o.status || "Pending"}</span>
                      </div>
                    </div>
                    <div className="divide-y divide-white/5">
                      {(o.items || []).map((it, i) => (
                        <div key={i} className="flex items-center gap-3 px-4 py-2 text-sm" data-testid={`coadmin-history-item-${o.id}-${i}`}>
                          <span className="flex-1 truncate">{it.fabric} <span className="text-white/40">·</span> {it.color}</span>
                          {it.delivered_kg !== null && it.delivered_kg !== undefined ? (
                            <div className="flex flex-col items-end shrink-0">
                              <span className="tabular text-[11px] text-white/50">Ordered: {fmt(it.quantity_kg)} {unitOf(it.fabric)}</span>
                              <span className="tabular text-emerald-400 font-medium" data-testid={`coadmin-history-delivered-${o.id}-${i}`}>Delivered: {fmt(it.delivered_kg)} {unitOf(it.fabric)}</span>
                            </div>
                          ) : (
                            <span className="tabular">{fmt(it.quantity_kg)} {unitOf(it.fabric)}</span>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
            {orders.length < historyTotal && (
              <div className="flex justify-center pt-2">
                <Button onClick={() => setHistoryLimit((l) => l + 20)} variant="outline" data-testid="coadmin-history-load-more" className="rounded-none h-10 px-8 border-white/20 bg-transparent text-white hover:bg-white hover:text-black text-xs uppercase tracking-wider transition-colors">
                  Load More ({historyTotal - orders.length} more)
                </Button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Add item dialog — mirrors customer add-to-order (keyboard-safe, stock-validated) */}
      <Dialog open={!!addItem} onOpenChange={(o) => { if (!o) { setAddItem(null); setQty(""); } }}>
        <DialogContent className="bg-[#0A0A0A] border border-white/10 text-white sm:max-w-md rounded-none flex flex-col max-h-[90dvh]" data-testid="coadmin-add-dialog">
          {addItem && (() => {
            const already = qtyInCart(addItem.fabric, addItem.color);
            const exceeds = parseFloat(qty) > 0 && parseFloat(qty) + already > (addItem.available_kg ?? 0);
            return (
              <>
                <DialogHeader>
                  <DialogTitle className="font-display font-black text-2xl tracking-tight text-white">Add to order</DialogTitle>
                  <DialogDescription className="text-white/50">{addItem.fabric}</DialogDescription>
                </DialogHeader>

                <div className="flex-1 overflow-y-auto -mx-1 px-1">
                  <div className="mt-2 flex items-center gap-3 p-4 border border-white/10 bg-[#141414]">
                    <span className="h-10 w-10 border border-white/20" style={{ backgroundColor: addItem.hex || SWATCHES[addItem.color] || "#666" }} />
                    <div>
                      <p className="text-sm text-white">{addItem.color}</p>
                      <p className="text-xs text-white/40" data-testid="coadmin-add-available">
                        Available: <span className={(addItem.available_kg || 0) > 0 ? "text-emerald-400" : "text-red-400"}>{(addItem.available_kg || 0) > 0 ? `${fmt(addItem.available_kg)} ${unitOf(addItem.fabric)}` : "Out of stock"}</span>
                      </p>
                    </div>
                  </div>

                  <div className="mt-4 space-y-2">
                    <Label className="text-xs uppercase tracking-wider text-white/50">Quantity ({unitOf(addItem.fabric)})</Label>
                    <Input
                      autoFocus
                      type="number"
                      step="0.5"
                      min="0"
                      inputMode="decimal"
                      value={qty}
                      onChange={(e) => setQty(e.target.value)}
                      onKeyDown={(e) => { if (e.key === "Enter" && !exceeds) confirmAdd(); }}
                      data-testid="coadmin-add-qty"
                      placeholder="e.g. 5"
                      className="rounded-none bg-[#141414] border-white/10 text-white placeholder:text-white/30 focus-visible:ring-2 focus-visible:ring-white/30"
                    />
                    {exceeds && (
                      <div data-testid="coadmin-low-stock-warning" className="mt-3 flex items-start gap-2 border border-amber-500/40 bg-amber-500/10 px-3 py-2.5 text-xs text-amber-300">
                        <PhoneCall className="h-4 w-4 shrink-0 mt-0.5" />
                        <span>This quantity isn't available right now. Please call 8010307363 / 7020006164 for the availability of the fabric.</span>
                      </div>
                    )}
                  </div>
                </div>

                {!exceeds && (
                  <Button onClick={confirmAdd} data-testid="coadmin-add-confirm" className="mt-4 shrink-0 w-full rounded-none bg-white text-black hover:bg-white/90 text-sm uppercase tracking-wider font-semibold">
                    Add to Order <Plus className="h-4 w-4 ml-2" />
                  </Button>
                )}
              </>
            );
          })()}
        </DialogContent>
      </Dialog>

      {/* Cart / order dialog — mirrors customer cart-sheet (scrollable body + pinned submit footer) */}
      <Dialog open={cartOpen} onOpenChange={setCartOpen}>
        <DialogContent className="bg-[#0A0A0A] border border-white/10 text-white sm:max-w-lg rounded-none flex flex-col max-h-[90dvh]" data-testid="coadmin-cart-sheet">
          <DialogHeader>
            <div className="flex items-center justify-between gap-3">
              <div>
                <DialogTitle className="font-display font-black text-2xl tracking-tight text-white">Your Order</DialogTitle>
                <DialogDescription className="text-white/50">Review the items, then submit for admin approval.</DialogDescription>
              </div>
              <div className="flex gap-2 shrink-0">
                <Button onClick={undo} disabled={!undoStack.length} data-testid="coadmin-undo" variant="outline" className="rounded-none h-8 px-2 border-white/20 bg-transparent text-white hover:bg-white hover:text-black disabled:opacity-30"><Undo2 className="h-4 w-4" /></Button>
                <Button onClick={redo} disabled={!redoStack.length} data-testid="coadmin-redo" variant="outline" className="rounded-none h-8 px-2 border-white/20 bg-transparent text-white hover:bg-white hover:text-black disabled:opacity-30"><Redo2 className="h-4 w-4" /></Button>
              </div>
            </div>
          </DialogHeader>

          <div className="mt-2 flex-1 overflow-y-auto thin-scroll -mx-1 px-1">
            {cart.length === 0 ? (
              <div className="py-16 text-center text-white/40 text-sm" data-testid="coadmin-cart-empty">No items yet. Tap <span className="text-white">Add</span> on any colour.</div>
            ) : (
              <div className="space-y-px bg-white/10 border border-white/10" data-testid="coadmin-cart">
                {cart.map((i) => {
                  const tid = `${slug(i.fabric)}-${slug(i.color)}`;
                  return (
                    <div key={`${i.fabric}|${i.color}`} className="bg-[#0A0A0A] p-3 flex items-center gap-3" data-testid={`coadmin-cart-item-${tid}`}>
                      <span className="h-9 w-9 border border-white/20 shrink-0" style={{ backgroundColor: i.hex || SWATCHES[i.color] || "#666" }} />
                      <div className="min-w-0 flex-1">
                        <p className="text-sm text-white truncate">{i.color}</p>
                        <p className="text-[11px] text-white/40 truncate">{i.fabric}</p>
                      </div>
                      <Input
                        type="number"
                        min="0"
                        step="0.5"
                        value={i.quantity_kg}
                        onChange={(e) => updateCartQty(i.fabric, i.color, e.target.value)}
                        data-testid={`coadmin-cart-qty-${tid}`}
                        className="w-20 h-9 rounded-none bg-[#141414] border-white/10 text-white text-right tabular focus-visible:ring-2 focus-visible:ring-white/30"
                      />
                      <span className="text-xs text-white/40">{unitOf(i.fabric)}</span>
                      <button onClick={() => removeItem(i.fabric, i.color)} data-testid={`coadmin-cart-remove-${tid}`} className="text-white/40 hover:text-red-400 transition-colors"><Trash2 className="h-4 w-4" /></button>
                    </div>
                  );
                })}
              </div>
            )}

            <div className="mt-4 border-t border-white/10 pt-4 mb-4 space-y-3">
              <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                <button
                  type="button"
                  onClick={() => setUrgent((v) => !v)}
                  data-testid="coadmin-order-urgent"
                  className={`flex items-center gap-2 px-3 py-2 border text-xs uppercase tracking-wider transition-colors ${urgent ? "border-red-500 bg-red-500/15 text-red-300" : "border-white/15 bg-[#0F0F0F] text-white/60 hover:border-white/40"}`}
                >
                  <span className={`h-3.5 w-3.5 border flex items-center justify-center ${urgent ? "bg-red-500 border-red-500" : "border-white/40"}`}>
                    {urgent && <CheckCircle2 className="h-3 w-3 text-white" />}
                  </span>
                  Mark Urgent
                </button>
                <div className="flex-1">
                  <Label className="text-[10px] uppercase tracking-[0.2em] text-white/40 block mb-1">Required by (optional)</Label>
                  <Input type="date" value={requiredBy} onChange={(e) => setRequiredBy(e.target.value)} data-testid="coadmin-order-requiredby" className="rounded-none h-9 bg-[#141414] border-white/10 text-white text-sm focus-visible:ring-2 focus-visible:ring-white/30" />
                </div>
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-[0.2em] text-white/40 block mb-1">Message to admin (optional)</Label>
                <textarea
                  value={message}
                  maxLength={499}
                  rows={2}
                  onChange={(e) => setMessage(e.target.value.slice(0, 499))}
                  data-testid="coadmin-order-message"
                  placeholder="Any note about this order for the CiNCStock team…"
                  className="w-full rounded-none bg-[#141414] border border-white/10 text-white text-sm p-2.5 placeholder:text-white/30 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/30 resize-none"
                />
                <p className="text-[10px] text-white/30 text-right tabular">{message.length}/499</p>
              </div>
            </div>
          </div>

          <div className="shrink-0 border-t border-white/10 pt-3">
            <div className="flex items-center justify-between mb-3">
              <span className="text-xs uppercase tracking-wider text-white/50">Total</span>
              <span className="tabular text-xl font-display font-black text-white" data-testid="coadmin-cart-total">{fmt(cartTotal)} {cartUnit}</span>
            </div>
            {customer && (
              <div className="p-3 border border-white/10 bg-[#0F0F0F] mb-3">
                <p className="text-[10px] uppercase tracking-[0.2em] text-white/40 mb-1">Order for</p>
                <p className="text-sm text-white">CO-AD — {customer.name}</p>
              </div>
            )}
            <Button onClick={submitOrder} disabled={submitting || cart.length === 0} data-testid="coadmin-submit-order" className="w-full rounded-none bg-white text-black hover:bg-white/90 text-sm uppercase tracking-wider font-semibold disabled:opacity-40">
              {submitting ? "Submitting…" : (<>Submit Order <ArrowRight className="h-4 w-4 ml-2" /></>)}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Submitted confirmation */}
      <Dialog open={!!submitted} onOpenChange={(o) => { if (!o) setSubmitted(null); }}>
        <DialogContent className="bg-[#0A0A0A] border border-white/10 rounded-none text-white" data-testid="coadmin-submitted-dialog">
          <DialogHeader>
            <DialogTitle className="font-display font-black text-2xl tracking-tight flex items-center gap-2"><CheckCircle2 className="h-6 w-6 text-emerald-400" /> Order placed</DialogTitle>
            <DialogDescription className="text-white/50">
              {submitted && <>Sent to admin for approval — CO-AD — {submitted.client_name}.</>}
            </DialogDescription>
          </DialogHeader>
          <Button onClick={() => setSubmitted(null)} data-testid="coadmin-submitted-close" className="rounded-none h-10 bg-white text-black hover:bg-white/90 text-sm uppercase tracking-wider font-semibold">Done</Button>
        </DialogContent>
      </Dialog>

      <ImageZoom src={zoomImg} alt="Catalogue" open={!!zoomImg} onOpenChange={(o) => { if (!o) setZoomImg(null); }} />

      <AlterItemDialog
        open={!!alterItemCtx}
        onOpenChange={(o) => { if (!o) setAlterItemCtx(null); }}
        stockOptions={stock}
        item={alterItemCtx?.item}
        onSave={handleCoadminAlterItem}
      />
      <CustomerPickerDialog
        open={!!alterCustomerCtx}
        onOpenChange={(o) => { if (!o) setAlterCustomerCtx(null); }}
        searchCustomers={async (q) => { if (!q || q.trim().length < 3) return []; return await coadminSearchCustomers(token, q.trim()); }}
        onPick={handleCoadminAlterCustomer}
      />
    </div>
  );
}
